/**
 * The anime CEL look, ported the only way it can be.
 *
 * ── Why this file exists ────────────────────────────────────────────────────
 *
 * The Blender source shades every surface through `NG_CelSurface`, which is
 * built on **Shader to RGB** — a node that reads the evaluated lighting result
 * and remaps it. Nothing in WebGL can do that: three has no hook that hands a
 * material the shaded value before tonemapping. The export's own README is
 * blunt about it — *"That cannot run in three.js. Do NOT try to port the node
 * group."*
 *
 * So the look is reproduced, not ported. `MeshToonMaterial` quantises N·L
 * against a gradient ramp, and `toon_gradient.png` IS the cel group's ramp — a
 * 64×1 three-step texture whose breakpoints (0.28 / 0.62) are the same ones the
 * Blender group steps at. Feed one to the other and the banding lands in the
 * same places, which is what actually sells the style. Everything the glTF
 * carries that toon has no concept of — metalness, roughness, specular — is
 * dropped on purpose; a cel surface has no specular term, and keeping one is
 * what made the grass shine under the floodlights.
 *
 * ── The one filter that matters ─────────────────────────────────────────────
 *
 * The ramp MUST sample with NearestFilter. On bilinear the three steps smear
 * back into the smooth gradient they were built to replace, and the result is
 * indistinguishable from plain diffuse — the single easiest way to silently
 * lose the whole look.
 */
import {
  Color, DoubleSide, FrontSide, Mesh, MeshToonMaterial, NearestFilter, RepeatWrapping,
  SRGBColorSpace, type Material, type Object3D, type Texture,
} from 'three';
import { directIrradianceFor } from '../lighting.js';

/** Materials whose look is deliberately NOT toon — emissive screens read as lit
 *  panels, not shaded surfaces, and toon-shading one turns the scoreboard off. */
const KEEP_ORIGINAL = /scoreboard|led|screen|emis/i;

/**
 * Irradiance a cel surface is authored to sit at.
 *
 * Blender shades `NG_CelSurface` at roughly unit exposure, so its base colours
 * are picked to be very close to the final pixel. three's rig is not neutral:
 * `sunIntensity` is deliberately tuned to put a PBR pitch near the top of the
 * ACES shoulder. `MeshToonMaterial` quantises N·L through the ramp but still
 * multiplies by the FULL irradiance, so those authored colours blow straight
 * past the ramp and every band flattens toward white — `GX_Grass_Matte` is
 * authored (0.118, 0.420, 0.145), a deep green, and was rendering pale mint.
 *
 * Normalising against the rig's own numbers (rather than baking in a constant)
 * means a future lighting retune re-normalises the cel look with it instead of
 * silently washing it out again. Correcting the STADIUM rather than the LIGHT is
 * deliberate: the rig is shared with the players and the ball, and the mismatch
 * is between two shading models, not a fault in the rig.
 *
 * The target itself is the one tuned value here — chosen so the rendered pitch
 * lands on its authored green.
 */
const CEL_TARGET_IRRADIANCE = 2.55;

const CEL_FILL_COMPENSATION = CEL_TARGET_IRRADIANCE / directIrradianceFor('day');

export interface ToonConversionStats {
  converted: number;
  skippedEmissive: number;
}

/**
 * Swap every mesh material under `root` for a toon equivalent sharing the same
 * base colour and maps. Returns what it touched so the caller can log honestly.
 */
export function applyToonLook(root: Object3D, gradient: Texture): ToonConversionStats {
  gradient.minFilter = NearestFilter;
  gradient.magFilter = NearestFilter;
  gradient.generateMipmaps = false;
  gradient.colorSpace = SRGBColorSpace;
  gradient.wrapS = RepeatWrapping;
  gradient.wrapT = RepeatWrapping;

  const stats: ToonConversionStats = { converted: 0, skippedEmissive: 0 };
  const cache = new Map<Material, MeshToonMaterial>();

  root.traverse((n) => {
    const mesh = n as Mesh;
    if (!(mesh as unknown as { isMesh?: boolean }).isMesh || !mesh.material) return;
    const list = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const out = list.map((src) => {
      if (KEEP_ORIGINAL.test(src.name)) {
        stats.skippedEmissive++;
        return src;
      }
      const hit = cache.get(src);
      if (hit) return hit;

      // Read whatever the glTF gave us, then discard the PBR half of it.
      const std = src as unknown as {
        color?: Color; map?: Texture | null; alphaMap?: Texture | null;
        emissive?: Color; emissiveMap?: Texture | null; emissiveIntensity?: number;
        transparent?: boolean; opacity?: number; alphaTest?: number; side?: number;
        vertexColors?: boolean; depthWrite?: boolean;
      };
      const toon = new MeshToonMaterial({
        color: (std.color?.clone() ?? new Color(0xffffff)).multiplyScalar(CEL_FILL_COMPENSATION),
        map: std.map ?? null,
        gradientMap: gradient,
        alphaMap: std.alphaMap ?? null,
        emissive: std.emissive?.clone() ?? new Color(0x000000),
        emissiveMap: std.emissiveMap ?? null,
        emissiveIntensity: std.emissiveIntensity ?? 1,
        transparent: std.transparent ?? false,
        opacity: std.opacity ?? 1,
        // Preserve the goal-net cutout: alphaTest is what keeps MASK mode MASK.
        alphaTest: std.alphaTest ?? 0,
        side: std.side === DoubleSide ? DoubleSide : FrontSide,
        vertexColors: std.vertexColors ?? false,
      });
      toon.name = `${src.name}__toon`;
      // Mown stripes — the export ships the pitch as flat `GX_Grass_Matte`
      // (its baked stripe texture was dropped as unused), but every Blender
      // render carries alternating mow bands. Recreated in-shader: bands run
      // ACROSS the pitch (alternating along world X, the sim's length axis) at
      // half the goal-area depth, ±6% luminance — matching the procedural
      // pitch this surface replaced. World X, not UV: the runoff grass shares
      // this material and the stripes must carry straight through it, which is
      // exactly how the export's own renders read ("striped run-off").
      if (/grass/i.test(src.name)) {
        toon.onBeforeCompile = (shader) => {
          shader.vertexShader = shader.vertexShader
            .replace('#include <common>', '#include <common>\nvarying vec3 vFgWorld;')
            .replace('#include <begin_vertex>', '#include <begin_vertex>\nvFgWorld = (modelMatrix * vec4(position, 1.0)).xyz;');
          shader.fragmentShader = shader.fragmentShader
            .replace('#include <common>', '#include <common>\nvarying vec3 vFgWorld;')
            .replace(
              '#include <map_fragment>',
              `#include <map_fragment>
               float fgStripe = step(0.5, fract(vFgWorld.x / 10.5));
               diffuseColor.rgb *= mix(0.94, 1.06, fgStripe);`,
            );
        };
        toon.customProgramCacheKey = (): string => 'fg-pitch-stripes-v1';
      }
      cache.set(src, toon);
      stats.converted++;
      return toon;
    });
    mesh.material = Array.isArray(mesh.material) ? out : out[0]!;
  });

  return stats;
}
